import React from 'react';
import _ from 'lodash';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';
import SchemaForm from 'app/components/general/SchemaForm';

import * as organisationsActions from 'app/ducks/organisations/actions';
import * as bankAccountActions from 'app/ducks/bankAccounts/actions';

import * as organisationsSelectors from 'app/ducks/organisations/selectors';
import * as bankAccountsSelectors from 'app/ducks/bankAccounts/selectors';

import SectionGroup from 'app/components/general/SectionGroup';
import HeaderThin from 'app/components/layout/HeaderThin';

const formSchema = {
  type: 'object',
  required: ['bankName', 'routingNumber', 'accountNumber', 'accountHolderName'],
  properties: {
    bankName: {
      type: 'string',
      title: 'Bank Name',
    },
    accountHolderName: {
      type: 'string',
      title: 'Account Holder Name',
    },
    routingNumber: {
      type: 'string',
      title: 'BSB',
      minLength: 4,
    },
    accountNumber: {
      type: 'string',
      title: 'Account Number',
      minLength: 4,
    },
  },
};

const selector = (state): any => ({
  currentOrganisation: organisationsSelectors.getCurrentOrganisation(state),
  currentBankAccount: bankAccountsSelectors.getCurrentFormBankAccount(state),
  bankAccounts: bankAccountsSelectors.getBankAccounts(state),
  isLoading: state.bankAccounts.isLoading,
  isSaving: state.bankAccounts.isSaving,
  hasSaved: state.bankAccounts.hasSaved,
  hasFailed: state.bankAccounts.hasFailed,
  errors: { ...state.bankAccounts.backendErrors },
});

const actionCreators = _.merge(organisationsActions, bankAccountActions);

interface IBankPayoutsProps {
  actions?: any;
  bankAccounts?: any;
  currentBankAccount?: any;
  currentOrganisation?: any;
  currentOrganisationId?: boolean;
  errors?: any;
  isLoading?: boolean;
  isSaving?: boolean;
  hasSaved?: boolean;
  hasFailed?: boolean;
}

export interface IBankPayoutsState {
  formData: any;
  originalFormData: any;
  changed: boolean;
  saved: boolean;
  errors: any;
}

@connect(selector, (dispatch) => ({
  actions: bindActionCreators(actionCreators, dispatch),
}))
export default class BankPayouts extends React.Component<
IBankPayoutsProps,
IBankPayoutsState
> {
  static defaultProps: IBankPayoutsProps = {
    actions: undefined,
    bankAccounts: {},
    currentBankAccount: {},
    currentOrganisation: {
      bankAccount: {},
    },
    errors: undefined,
    isLoading: false,
    isSaving: false,
    hasSaved: false,
    hasFailed: false,
  };

  state: IBankPayoutsState = {
    changed: false,
    saved: false,
    formData: {},
    originalFormData: {},
    errors: {},
  };

  componentDidMount(): void {
    this.props.actions.getBankAccount(
      this.props.currentOrganisation.bankAccount.id,
    );
  }

  static getDerivedStateFromProps(nextProps, prevState): any {
    if (!prevState.changed) {
      const formData = {
        bankName: nextProps.currentBankAccount.bankName || undefined,
        routingNumber: nextProps.currentBankAccount.routingNumber || undefined,
        accountNumber: nextProps.currentBankAccount.accountNumber || undefined,
        accountHolderName: nextProps.currentBankAccount.accountHolderName || undefined,
        country:
          nextProps.currentBankAccount && nextProps.currentBankAccount.country
            ? nextProps.currentBankAccount.country.id
            : 'AUS',
      };
      return {
        ...prevState,
        formData,
        originalFormData: formData,
        errors: nextProps.errors,
      };
    }
    return null;
  }

  isValid = (): boolean => _.isEmpty(this.state.errors) && this.state.changed;

  handleChange = (e): void => {
    const changed = this.state.originalFormData !== e.formData;
    this.setState(() => ({
      formData: e.formData,
      errors: [],
      changed,
    }));
  };

  handleError = (errors): void => {
    this.setState({ errors });
  }

  handleSubmit = (e): void => {
    if (this.isValid()) {
      if (this.props.currentBankAccount.id == null) {
        this.props.actions.createBankAccount({
          bankName: this.state.formData.bankName,
          routingNumber: this.state.formData.routingNumber,
          accountNumber: this.state.formData.accountNumber,
          accountHolderName: this.state.formData.accountHolderName,
          organisation: {
            id: this.props.currentOrganisation?.id,
          },
          country: {
            id: 'AUS',
          },
        });
      } else {
        this.props.actions.createBankAccount({
          bankName: this.state.formData.bankName,
          routingNumber: this.state.formData.routingNumber,
          accountNumber: this.state.formData.accountNumber,
          accountHolderName: this.state.formData.accountHolderName,
          organisation: {
            id: this.props.currentOrganisation?.id,
          },
          country: {
            id: 'AUS',
          },
        });
      }
    }
    this.setState({ changed: false });
  };

  render(): React.ReactNode {
    return (
      <div>
        <div className="row">
          <div className="col">
            <HeaderThin
              breadcrumb="Settings"
              breadcrumbLink="/settings"
              title="Bank Payouts"
              divider
            />
          </div>
        </div>
        <div className="row">
          <div className="col col-12 col-lg-8 col-xlg-6">
            <SectionGroup title="Bank Information">
              <div className="row">
                <div className="col">
                  <span className="form-description">
                    All payments received will be sent to this bank account
                    daily. Bank settlements are made up of payments received
                    minus Teammo fees.
                  </span>
                </div>
              </div>
              <div className="row with-margin-top">
                <div className="col">
                  <SchemaForm
                    schema={formSchema}
                    handleChange={this.handleChange}
                    handleError={this.handleError}
                    handleSubmit={this.handleSubmit}
                    formData={this.state.formData}
                    isValid={this.isValid()}
                    isLoading={this.props.isSaving}
                    hasSaved={this.props.hasSaved}
                    hasFailed={this.props.hasFailed}
                    originalFormData={this.state.originalFormData}
                  />
                </div>
              </div>
            </SectionGroup>
          </div>
        </div>
      </div>
    );
  }
}
